import * as React from "react";
import {
  Button,
  Typography,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  Box,
  PopperPlacementType,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { visuallyHidden } from "@mui/utils";

interface Props {
  id: string;
  label: string;
  popperPosition: PopperPlacementType;
  children: React.ReactNode;
  hover?: boolean;
  wide?: number;
  buttonStyle?: React.CSSProperties;
  HHICalculation?: string;
  pgcSideBar?: boolean;
  pgcPopup?: boolean;
}

const useStyle = makeStyles(theme => ({
  hhiCalculation: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
    padding: "5px",
  },
  popperStyles: {
    zIndex: 100,
    [theme.breakpoints.down("md")]: {
      width: 200,
    },
  },
  infoButton: {
    "height": 40,
    "marginTop": 0,
    "marginLeft": 3,
    "overflow": "visible",
    "fontSize": "1.5rem",
    "minWidth": 40,
    "textAlign": "center",
    "borderRadius": "50%",
    "cursor": "pointer",
    "&.Mui-focusVisible": { backgroundColor: "rgba(0,0,0,.08)" },
  },
  pgcSideBar: {
    "minWidth": 0,
    "height": 40,
    "width": 40,
    "marginTop": 0,
    "overflow": "visible",
    "fontSize": "1.5rem",
    "textAlign": "center",
    "borderRadius": "50%",
    "cursor": "pointer",
    "&.Mui-focusVisible": { backgroundColor: "rgba(0,0,0,.08)" },
  },
  popup: {
    "border": "1px solid",
    "borderColor": theme.palette.secondary.dark,
    "maxWidth": 825,
    "boxShadow": "5px 5px 10px  #CED6E3",
    "padding": "1rem",
    "& p:not(:last-child)": {
      paddingBottom: "1rem",
    },
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "underline",
    },
  },
  pgcPopup: {
    "border": "1px solid",
    "color": theme.palette.primary.main,
    "borderColor": theme.palette.secondary.dark,
    "maxWidth": 437,
    "boxShadow": "5px 5px 10px  #CED6E3",
    "padding": "1rem",
    "& p:not(:last-child)": {
      paddingBottom: "1rem",
    },
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "underline",
    },
  },
}));
export default function MoreInfoButton({
  id,
  label,
  popperPosition,
  children,
  hover,
  wide,
  buttonStyle,
  HHICalculation,
  pgcSideBar,
  pgcPopup,
}: Props) {
  const classes = useStyle();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const handleToggle = () => {
    setOpen(!open);
  };

  const buttonProps = hover
    ? {
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
        onFocus: () => setOpen(true),
        onBlur: () => setOpen(false),
        onClick: () => handleToggle(),
      }
    : { onClick: () => handleToggle() };

  return (
    <>
      <Button
        {...buttonProps}
        ref={anchorRef}
        aria-controls={open ? id : undefined}
        aria-haspopup="true"
        className={pgcSideBar ? classes.pgcSideBar : classes.infoButton}
        style={buttonStyle}
      >
        <Typography className={HHICalculation && classes.hhiCalculation}>
          {HHICalculation}
        </Typography>
        <HelpOutlineOutlinedIcon style={{ zIndex: 10 }} />
        <svg height="24" width="24" style={{ marginLeft: -24 }}>
          <circle cx="12" cy="12" r="10" fill="white" />
        </svg>
        <Typography sx={visuallyHidden}>{label}</Typography>
      </Button>
      <Popper
        style={wide ? { width: wide } : undefined}
        className={classes.popperStyles}
        open={open}
        anchorEl={anchorRef.current}
        placement={popperPosition}
        transition
        disablePortal
      >
        {({ TransitionProps }) =>
          open ? (
            <Grow {...TransitionProps}>
              <Paper className={pgcPopup ? classes.pgcPopup : classes.popup}>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                  <Box id={id}>{children}</Box>
                </ClickAwayListener>
              </Paper>
            </Grow>
          ) : (
            undefined
          )
        }
      </Popper>
    </>
  );
}
